"use client";

import Link from "@/components/i18n/link";
import { Menu, UserCircle } from "lucide-react";
import { useState } from "react";
import { ThemeToggle } from "./ThemeToggle";
import { useAuthStore } from "@/lib/store/auth-store";
import { NotificationBell } from "./NotificationBell";
import { LanguageSwitcher } from "./i18n/language-switcher";
import { CountrySwitcher } from "./i18n/country-switcher";
import { useLocalizedPathname, useMessages } from "@/lib/i18n/client";
import { cn } from "@/lib/utils";
import { trackGamePromoClick } from "@/lib/tracking/game-funnel";
import messages from "./Header.i18n";

interface NavItem {
  href: string;
  label: string;
  // Otras rutas que cuentan como esta sección.
  also?: string[];
  onClick?: () => void;
}

// Sección del menú en la que está la persona (la ruta o cualquier subruta).
function isActive(pathname: string, item: NavItem): boolean {
  return [item.href, ...(item.also ?? [])].some(
    (href) => pathname === href || pathname.startsWith(`${href}/`),
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const t = useMessages(messages);
  const pathname = useLocalizedPathname();
  const navItems: NavItem[] = [
    // El detalle de un evento (/evento/…) también es parte de "Explorar".
    { href: "/eventos", label: t.explore, also: ["/evento"] },
    { href: "/organizaciones", label: t.organizations },
    { href: "/sugerencias", label: t.suggestions },
    { href: "/viva-cine", label: "Viva Cine" },
    // Portada con todos los juegos; activo también dentro de cada uno.
    { href: "/juegos", label: t.games, onClick: () => trackGamePromoClick("hub", "menu") },
  ];
  return (
    <header className="sticky top-0 z-[1100] border-b border-border bg-background/85 backdrop-blur-xl">
      <div className="container-page flex h-16 items-center justify-between gap-6">
        <Link href="/" className="flex items-center gap-2">
          {/* SVG local: el optimizador de next/image no aporta nada acá. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo.svg" alt="Ciudad Viva" className="h-8 w-auto" />
          <span className="text-lg font-semibold tracking-tight">Ciudad Viva</span>
        </Link>

        {/* Menú completo desde lg: entre md y lg ya no cabía (se desbordaba en tablets), ahí se usa el desplegable. */}
        <nav className="hidden items-center gap-7 text-sm font-medium lg:flex">
          {navItems.map((item) => {
            const active = isActive(pathname, item);
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={item.onClick}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative whitespace-nowrap py-1 transition hover:text-foreground",
                  active
                    ? "text-foreground after:absolute after:-bottom-[21px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-primary"
                    : "text-muted-foreground",
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {user && <NotificationBell />}
          {user ? (
            <Link
              href="/dashboard"
              className="hidden items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium transition hover:border-foreground/40 lg:inline-flex"
            >
              <UserCircle className="h-4 w-4" />
              {user.name}
            </Link>
          ) : (
            <>
              <Link
                href="/login"
                className="hidden rounded-full border border-border px-4 py-2 text-sm font-medium transition hover:border-foreground/40 lg:inline-flex"
              >
                {t.login}
              </Link>
              <Link
                href="/registro"
                className="hidden rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition hover:bg-foreground/85 lg:inline-flex"
              >
                {t.publish}
              </Link>
            </>
          )}
          <CountrySwitcher className="hidden lg:flex" />
          <LanguageSwitcher className="hidden lg:flex" />
          <ThemeToggle />
          <button
            onClick={() => setOpen(!open)}
            aria-label={t.menu}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border lg:hidden"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border bg-surface lg:hidden">
          <nav className="container-page flex flex-col gap-1 py-3 text-sm font-medium">
            {navItems.map((item) => {
              const active = isActive(pathname, item);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={item.onClick}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "rounded-lg px-3 py-2 hover:bg-muted",
                    active && "bg-primary/10 text-primary",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="mt-1 border-t border-border pt-1">
              {user ? (
                <Link href="/dashboard" className="rounded-lg px-3 py-2 hover:bg-muted block font-medium">
                  {user.name} — {t.myPanel}
                </Link>
              ) : (
                <>
                  <Link href="/login" className="rounded-lg px-3 py-2 hover:bg-muted block">
                    {t.login}
                  </Link>
                  <Link href="/registro" className="rounded-lg px-3 py-2 hover:bg-muted block font-medium">
                    {t.publish}
                  </Link>
                </>
              )}
            </div>
            <div className="mt-1 flex items-center justify-between border-t border-border px-3 pt-3">
              <LanguageSwitcher />
              <CountrySwitcher />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
